/**
 * @file AI Gallery「发布成功」浏览链接弹窗
 *
 * 发布表单提交成功后弹出，展示生成的可访问网页链接，
 * 提供「复制」与「关闭」操作（与 ChatShareModal 分享链接弹窗保持一致风格）。
 */
import React, { useEffect, useState } from 'react';
import { Modal } from 'tea-component';
import { useIntl } from 'umi';
import SvgIcon from '@home/components/SvgIcon';
import CommonBubble from '@home/components/CommonBubble';
import { copyToClipboard } from '@/pages/aigc/utils/utils';
import './index.scss';

export interface IAiGalleryShareLinkModalProps {
  /** 是否展示弹窗 */
  visible: boolean;
  /** 发布成功后生成的可访问链接 */
  url: string;
  /** 关闭弹窗 */
  onClose: () => void;
}

/**
 * AI Gallery 发布成功后的「浏览链接」弹窗
 * visible=false 时不渲染任何内容
 */
export const AiGalleryShareLinkModal: React.FC<IAiGalleryShareLinkModalProps> = ({
  visible,
  url,
  onClose,
}) => {
  const { formatMessage } = useIntl();
  const [copied, setCopied] = useState(false);

  // 弹窗重新打开时重置复制状态
  useEffect(() => {
    if (visible) setCopied(false);
  }, [visible]);

  // 复制成功提示自动消失
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 3000);
    return () => clearTimeout(timer);
  }, [copied]);

  /** 复制链接到剪贴板 */
  const handleCopy = async (): Promise<void> => {
    if (!url) return;
    const success = await copyToClipboard(url);
    if (success) setCopied(true);
  };

  if (!visible) return null;

  return (
    <Modal
      className="dba-ai-gallery-share-link-modal dba-chat-share-dialog"
      caption={formatMessage({ id: 'dba_ai_gallery_publish_success' })}
      visible={visible}
      onClose={onClose}
    >
      <Modal.Body>
        <div className="dba-chat-share-input-wrapper">
          <input id="dba-input-link" type="text" readOnly value={url} />
          <CommonBubble
            className="dba-chat-share-copy-bubble"
            content={formatMessage({ id: 'dba_common_copy_success' })}
            placementOffset={[0, 0]}
            placement="left"
            visible={copied}
            arrowPointAtCenter
            overlayClassName="dba-chat-share-overlay"
          >
            <button
              type="button"
              disabled={copied}
              className="dba-input-btn dba-btn-default dba-share-link-copy-btn"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={handleCopy}
            >
              <SvgIcon type="copy-new" />
              {formatMessage({ id: 'dba_common_copy' })}
            </button>
          </CommonBubble>
        </div>

        <div className="dba-chat-share-caption">
          {formatMessage({ id: 'dba_menu_share_caption' })}
        </div>

        <div className="dba-ai-gallery-share-link-modal__footer">
          <button
            type="button"
            className="dba-ai-gallery-share-link-modal__close-btn"
            onClick={onClose}
          >
            {formatMessage({ id: 'dba_close' })}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AiGalleryShareLinkModal;
